import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PageHeader } from '@carrent/ui'
import { api } from '@carrent/api'

const TYPE_ICONS = {
  refuel:      '⛽',
  maintenance: '🔧',
  wash:        '🫧',
  other:       '📝',
}
const TYPE_COLORS = {
  refuel:      '#f59e0b',
  maintenance: '#ef4444',
  wash:        '#3b82f6',
  other:       '#6b7280',
}

function DonutChart({ data, total }) {
  if (!total) return null
  const r = 52
  const circ = 2 * Math.PI * r
  let offset = 0

  const segments = Object.entries(data).filter(([, v]) => v > 0).map(([k, v]) => {
    const pct   = v / total
    const dash  = circ * pct
    const seg   = { key: k, value: v, pct, dash, offset, color: TYPE_COLORS[k] }
    offset += dash
    return seg
  })

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap)', flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: 120, height: 120, flexShrink: 0 }}>
        <svg width="120" height="120" style={{ transform: 'rotate(-90deg)' }}>
          <circle cx="60" cy="60" r={r} fill="none" stroke="var(--border)" strokeWidth="14" />
          {segments.map(s => (
            <circle key={s.key} cx="60" cy="60" r={r} fill="none"
              stroke={s.color} strokeWidth="14"
              strokeDasharray={`${s.dash - 1} ${circ - s.dash + 1}`}
              strokeDashoffset={-s.offset}
            />
          ))}
        </svg>
        <div style={{ position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: '0.9rem', fontWeight: 800 }}>
            {(total / 1000).toFixed(0)}к
          </span>
          <span style={{ fontSize: '0.62rem', color: 'var(--text-muted)' }}>₽</span>
        </div>
      </div>
      {/* Легенда */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
        {segments.map(s => (
          <div key={s.key} style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
              <span style={{ fontSize: '0.82rem' }}>
                {TYPE_ICONS[s.key]} {s.key === 'refuel' ? 'Топливо'
                  : s.key === 'maintenance' ? 'ТО' : s.key === 'wash' ? 'Мойка' : 'Прочее'}
              </span>
            </div>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: s.color }}>
              {s.value.toLocaleString()} ₽
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Анализ расхода топлива ───────────────────────────────────────────────────

function FuelChart({ segments }) {
  if (!segments?.length) return null

  const W = 300, H = 140, padX = 30, padY = 20
  const values = segments.map(s => s.consumption)
  const minV = Math.min(...values) * 0.9
  const maxV = Math.max(...values) * 1.1
  const range = maxV - minV || 1

  // Точки
  const points = segments.map((s, i) => {
    const x = padX + (i / Math.max(1, segments.length - 1)) * (W - padX * 2)
    const y = H - padY - ((s.consumption - minV) / range) * (H - padY * 2)
    return { x, y, ...s }
  })

  // Линия
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')

  // Заливка под линией
  const areaPath = path + ` L ${points[points.length-1].x} ${H - padY} L ${points[0].x} ${H - padY} Z`

  // Линии сетки (3 уровня)
  const gridY = [0.25, 0.5, 0.75].map(p => ({
    y: padY + p * (H - padY * 2),
    val: maxV - p * range,
  }))

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {/* Сетка */}
      {gridY.map((g, i) => (
        <g key={i}>
          <line x1={padX} y1={g.y} x2={W - padX} y2={g.y}
            stroke="var(--border)" strokeWidth="0.5" strokeDasharray="2 3" />
          <text x={4} y={g.y + 3} fontSize="9" fill="var(--text-muted)">
            {g.val.toFixed(1)}
          </text>
        </g>
      ))}
      {/* Заливка */}
      <path d={areaPath} fill="rgba(245,158,11,0.12)" />
      {/* Линия */}
      <path d={path} fill="none" stroke="#f59e0b" strokeWidth="2"
        strokeLinejoin="round" strokeLinecap="round" />
      {/* Точки */}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="3.5" fill="#f59e0b" stroke="var(--bg)" strokeWidth="1.5" />
          <title>
            {p.consumption} л/100км · {p.distance} км · {new Date(p.from_date).toLocaleDateString('ru')}
          </title>
        </g>
      ))}
    </svg>
  )
}

function FuelSection({ fuel }) {
  return (
    <>
      <p className="section-title">⛽ Расход топлива</p>

      {/* Качество данных */}
      {!fuel.has_enough_data ? (
        <div className="card" style={{
          background: 'rgba(245,158,11,0.05)',
          borderColor: 'rgba(245,158,11,0.3)',
        }}>
          <p style={{ fontSize: '0.88rem', fontWeight: 600 }}>
            📊 Недостаточно данных для расчёта
          </p>
          <p className="text-muted" style={{ fontSize: '0.78rem', marginTop: 6, lineHeight: 1.5 }}>
            Заправок: <b>{fuel.refuels_count}</b> · из них «до полного»: <b>{fuel.full_tank_count}</b>
            <br/>
            Для расчёта расхода нужно минимум <b>2 заправки до полного бака</b> с указанием пробега.
            Расход считается между такими заправками.
          </p>
        </div>
      ) : (
        <>
          {/* Ключевые метрики расхода */}
          <div className="stat-grid">
            <div className="stat-box">
              <div className="stat-box__val" style={{ color: '#f59e0b', fontSize: '1.4rem' }}>
                {fuel.avg_consumption}
              </div>
              <div className="stat-box__label">л / 100 км</div>
            </div>
            <div className="stat-box">
              <div className="stat-box__val" style={{ fontSize: '1.4rem' }}>
                {fuel.cost_per_km?.toFixed(1) ?? '—'}
              </div>
              <div className="stat-box__label">₽ / км</div>
            </div>
            <div className="stat-box">
              <div className="stat-box__val" style={{ fontSize: '1.4rem' }}>
                {fuel.min_consumption}
              </div>
              <div className="stat-box__label">мин л/100</div>
            </div>
            <div className="stat-box">
              <div className="stat-box__val" style={{ fontSize: '1.4rem' }}>
                {fuel.max_consumption}
              </div>
              <div className="stat-box__label">макс л/100</div>
            </div>
          </div>

          {/* График */}
          {fuel.segments?.length > 0 && (
            <div className="card">
              <p style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 8 }}>
                Динамика расхода
              </p>
              <FuelChart segments={fuel.segments} />
              <p className="text-muted" style={{ fontSize: '0.7rem', marginTop: 8, textAlign: 'center' }}>
                {fuel.segments.length} сегмента между заправками «до полного»
              </p>
            </div>
          )}
        </>
      )}

      {/* Список заправок */}
      <p className="section-title" style={{ marginTop: 'var(--gap)' }}>
        История заправок ({fuel.refuels_count})
      </p>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {fuel.refuels.slice().reverse().map((r, i) => (
          <div key={r.event_id} style={{
            padding: '10px var(--gap)',
            borderBottom: i < fuel.refuels.length - 1 ? '1px solid var(--border)' : 'none',
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>
                  {r.liters} л
                </span>
                {r.is_full_tank && (
                  <span style={{
                    fontSize: '0.65rem', padding: '1px 6px', borderRadius: 99,
                    background: 'rgba(245,158,11,0.15)', color: '#f59e0b', fontWeight: 700,
                  }}>
                    🔋 До полного
                  </span>
                )}
              </div>
              <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>
                {r.station_name && <>{r.station_name} · </>}
                {r.mileage > 0 && <>{r.mileage.toLocaleString()} км · </>}
                {new Date(r.created_at).toLocaleDateString('ru', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <p style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--warning)' }}>
                {r.amount.toLocaleString()} ₽
              </p>
              {r.amount > 0 && r.liters > 0 && (
                <p className="text-muted" style={{ fontSize: '0.7rem' }}>
                  {(r.amount / r.liters).toFixed(2)} ₽/л
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}


export default function AdminCarFinance() {
  const { id } = useParams()
  const [data, setData]     = useState(null)
  const [fuel, setFuel]     = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      api.get(`/admin/cars/${id}/finance`).catch(() => ({ data: { data: null } })),
      api.get(`/admin/cars/${id}/fuel-analysis`).catch(() => ({ data: { data: null } })),
    ]).then(([f, fa]) => {
      setData(f.data?.data)
      setFuel(fa.data?.data)
    }).finally(() => setLoading(false))
  }, [id])

  if (loading) return (
    <div>
      <PageHeader title="Финансы" backTo={`/admin/cars/${id}`} />
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    </div>
  )

  if (!data) return (
    <div><PageHeader title="Нет данных" backTo={`/admin/cars/${id}`} /></div>
  )

  const { car, summary, monthly } = data
  const hasData = summary.trips_count > 0

  return (
    <div style={{ paddingBottom: 'var(--gap-xl)' }}>
      <PageHeader
        title={`${car.brand} ${car.model}`}
        backTo={`/admin/cars/${id}`}
        right={
          <span style={{ fontSize: '0.78rem', fontFamily: 'monospace',
            background: '#fff', color: '#000', padding: '2px 8px',
            borderRadius: 4, border: '1px solid #333', fontWeight: 800 }}>
            {car.plate}
          </span>
        }
      />

      <div className="p-page flex-col" style={{ gap: 'var(--gap)' }}>

        {!hasData ? (
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <p style={{ fontSize: '2.5rem', marginBottom: 12 }}>📊</p>
            <p style={{ fontWeight: 600 }}>Нет завершённых поездок</p>
            <p className="text-muted" style={{ fontSize: '0.88rem', marginTop: 6 }}>
              Финансовая аналитика появится после первой поездки
            </p>
          </div>
        ) : (
          <>
            {/* Ключевые метрики */}
            <p className="section-title">Сводка</p>
            <div className="stat-grid">
              {[
                { label: 'Поездок',  value: summary.trips_count,                       unit: '' },
                { label: 'Пробег',   value: summary.total_distance.toLocaleString(),    unit: ' км' },
                { label: 'Расходы',  value: summary.total_expenses.toLocaleString(),    unit: ' ₽', color: 'var(--warning)' },
                { label: 'Л/100км', value: summary.avg_consumption ?? '—',             unit: summary.avg_consumption ? '' : '' },
              ].map(({ label, value, unit, color }) => (
                <div key={label} className="stat-box">
                  <div className="stat-box__val" style={color ? { color, fontSize: '1.4rem' } : { fontSize: '1.4rem' }}>
                    {value}{unit}
                  </div>
                  <div className="stat-box__label">{label}</div>
                </div>
              ))}
            </div>

            {/* Доп. метрики */}
            {summary.total_fuel_l > 0 && (
              <div className="card" style={{ display: 'flex', justifyContent: 'space-around',
                textAlign: 'center', padding: 'var(--gap-sm) var(--gap)' }}>
                <div>
                  <p style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    {summary.total_fuel_l.toFixed(0)} л
                  </p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>Топлива заправлено</p>
                </div>
                <div style={{ width: 1, background: 'var(--border)' }} />
                <div>
                  <p style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                    {summary.expenses_by_type.refuel > 0
                      ? `${(summary.expenses_by_type.refuel / summary.total_fuel_l).toFixed(0)} ₽`
                      : '—'}
                  </p>
                  <p className="text-muted" style={{ fontSize: '0.75rem' }}>₽ за литр (ср.)</p>
                </div>
              </div>
            )}

            {/* Диаграмма расходов */}
            {summary.total_expenses > 0 && (
              <>
                <p className="section-title">Структура расходов</p>
                <div className="card">
                  <DonutChart data={summary.expenses_by_type} total={summary.total_expenses} />
                </div>

                {/* Детальная разбивка */}
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  {Object.entries(summary.expenses_by_type)
                    .filter(([, v]) => v > 0)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, amount]) => {
                      const pct = Math.round(amount / summary.total_expenses * 100)
                      return (
                        <div key={type} style={{ padding: '11px var(--gap)',
                          borderBottom: '1px solid var(--border)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between',
                            marginBottom: 5 }}>
                            <span style={{ fontSize: '0.88rem' }}>
                              {TYPE_ICONS[type]} {summary.expenses_labels[type]}
                            </span>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontWeight: 700, color: TYPE_COLORS[type] }}>
                                {amount.toLocaleString()} ₽
                              </span>
                              <span className="text-muted" style={{ fontSize: '0.75rem', marginLeft: 6 }}>
                                {pct}%
                              </span>
                            </div>
                          </div>
                          <div style={{ height: 4, background: 'var(--border)', borderRadius: 99 }}>
                            <div style={{ height: '100%', width: `${pct}%`,
                              background: TYPE_COLORS[type], borderRadius: 99,
                              transition: 'width 0.5s ease' }} />
                          </div>
                        </div>
                      )
                    })}
                </div>
              </>
            )}

            {/* По месяцам */}
            {monthly.length > 0 && (
              <>
                <p className="section-title">По месяцам</p>
                <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  {monthly.map(m => (
                    <div key={m.month} style={{ padding: '10px var(--gap)',
                      borderBottom: '1px solid var(--border)',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{ fontSize: '0.88rem', fontWeight: 600 }}>
                          {new Date(m.month + '-01').toLocaleDateString('ru', { month: 'long', year: 'numeric' })}
                        </p>
                        <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>
                          {m.trips} поездок · {m.distance.toLocaleString()} км
                        </p>
                      </div>
                      <span style={{ fontWeight: 700, color: m.expenses > 0 ? 'var(--warning)' : 'var(--text-muted)' }}>
                        {m.expenses > 0 ? `${m.expenses.toLocaleString()} ₽` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Расход топлива — вне блока hasData */}
        {fuel && fuel.refuels_count > 0 && (
          <FuelSection fuel={fuel} />
        )}

      </div>
    </div>
  )
}
